
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useToast } from '../hooks/useToast';
import { Project, Role, User } from '../types';
import { db } from '../services';
import { generateId } from '../utils/formatters';
import { sanitizeInput } from '../utils/validation';

interface FeedbackState {
  message: string;
  type: 'success' | 'error';
}

const Projects: React.FC = () => {
  const { currentUser } = useAuth();
  const { projects, tasks, setProjects, addTimelineEvent, addNotification, users } = useProject();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'active' | 'archived'>('active');
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [newProject, setNewProject] = useState({ 
    name: '', 
    description: '', 
    deadline: new Date().toISOString().split('T')[0] 
  });
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const isAdminOrManager = currentUser?.role === Role.ADMIN || currentUser?.role === Role.MANAGER;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    setIsSubmitting(true);
    setFeedback(null);

    const finalMemberIds = Array.from(new Set([currentUser.id, ...selectedMembers]));

    const project: Project = {
      id: crypto.randomUUID(), 
      name: newProject.name,
      description: newProject.description,
      ownerId: currentUser.id,
      memberIds: finalMemberIds,
      createdAt: new Date().toISOString(),
      deadline: newProject.deadline,
      status: 'active'
    };

    try {
      await db.createProject(project);
      setProjects(prev => [...prev, project]);
      
      setFeedback({ message: '✨ Project created successfully!', type: 'success' });
      
      // These are non-critical, don't block on them
      try {
        await addTimelineEvent(project.id, 'project_created', `Project "${project.name}" was initialized.`);
      } catch (e) {
        console.warn('Timeline event failed:', e);
      }

      // Notify the creator
      try {
        await addNotification('New Project', `Project "${project.name}" has been created.`, currentUser.id, `/projects/${project.id}`, false);
      } catch (e) {
        console.warn('Notification failed:', e);
      }

      // Notify all assigned members (except the creator)
      try {
        const membersToNotify = project.memberIds.filter(id => id !== currentUser.id);
        for (const memberId of membersToNotify) {
          await addNotification(
            '📋 Added to Project',
            `You've been added to project "${project.name}"`,
            memberId,
            `/projects/${project.id}`,
            true // Send push notification
          );
        }
      } catch (e) {
        console.warn('Member notifications failed:', e);
      }

      setTimeout(() => {
        setShowModal(false);
        setNewProject({ name: '', description: '', deadline: new Date().toISOString().split('T')[0] });
        setSelectedMembers([]);
        setFeedback(null);
        navigate(`/projects/${project.id}`);
      }, 800);

    } catch (err: any) {
      console.error('Project creation error:', err);
      setFeedback({ 
        message: `Error: ${err.message || 'Failed to create project.'}`, 
        type: 'error' 
      });
      setIsSubmitting(false);
    }
  };

  const toggleMember = (userId: string) => {
    setSelectedMembers(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const filteredProjects = projects.filter(p => {
    const isStatusMatch = activeFilter === 'active' ? p.status !== 'archived' : p.status === 'archived';
    // Admin can see all projects, Managers and Users only see projects they're members of
    const canSeeAll = currentUser?.role === Role.ADMIN;
    const isMember = p.memberIds.includes(currentUser?.id || '');
    return isStatusMatch && (canSeeAll || isMember);
  }) || [];

  return (
    <div className="animate-in fade-in duration-500 space-y-8 overflow-y-auto h-full p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Ostool Albon Projects</h1>
          <p className="text-slate-500 mt-1 font-medium">Synced Cloud Architecture • Real-time Monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-slate-100 p-1.5 rounded-2xl flex gap-1">
            <button 
              onClick={() => setActiveFilter('active')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeFilter === 'active' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Active
            </button>
            <button 
              onClick={() => setActiveFilter('archived')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeFilter === 'archived' ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Archive
            </button>
          </div>
          {isAdminOrManager && activeFilter === 'active' && (
            <button 
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20 active:scale-95"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
              New Project
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 pb-12">
        {filteredProjects.length === 0 ? (
          <div className="col-span-full py-24 text-center bg-white rounded-[2.5rem] border-2 border-dashed border-slate-200">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Workspace Empty</h3>
            <p className="text-slate-500 font-medium max-w-xs mx-auto">No {activeFilter} projects matched your visibility level.</p>
          </div>
        ) : (
          filteredProjects.map(project => {
            const projectTasks = tasks.filter(t => t.projectId === project.id) || [];
            const completion = projectTasks.length > 0 
              ? Math.round((projectTasks.filter(t => t.status === 'DONE').length / projectTasks.length) * 100)
              : 0;

            return (
              <div 
                key={project.id} 
                onClick={() => navigate(`/projects/${project.id}`)}
                className="bg-white rounded-[2rem] border border-slate-100 p-8 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all cursor-pointer group relative overflow-hidden"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white`}>
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
                  </div>
                  <span className={`px-3 py-1 text-[10px] font-black uppercase rounded-lg tracking-widest ${
                    project.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 
                    project.status === 'archived' ? 'bg-rose-100 text-rose-700' : 
                    'bg-indigo-100 text-indigo-700'
                  }`}>
                    {project.status}
                  </span>
                </div>
                
                <h3 className="text-2xl font-black text-slate-900 mb-3 group-hover:text-indigo-600 transition-colors">{project.name}</h3>
                <p className="text-sm text-slate-500 font-medium line-clamp-2 h-10 mb-8 leading-relaxed">{project.description}</p>
                
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Completion</span>
                    <span className="text-sm font-black text-slate-900">{completion}%</span>
                  </div>
                  <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-1000 bg-gradient-to-r from-indigo-500 to-purple-500`}
                      style={{ width: `${completion}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-xl rounded-[2rem] md:rounded-[2.5rem] shadow-2xl p-6 md:p-10 max-h-[90vh] overflow-y-auto no-scrollbar relative">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <h2 className="text-xl md:text-3xl font-black text-slate-900 tracking-tight">Create New Project</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors p-2">
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            
            <form onSubmit={handleCreate} className="space-y-4 md:space-y-6">
              {feedback && (
                <div className={`p-3 md:p-4 rounded-xl text-xs md:text-sm font-bold flex items-center gap-3 animate-in slide-in-from-top-2 ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>
                  <div className={`w-2 h-2 rounded-full ${feedback.type === 'success' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                  {feedback.message}
                </div>
              )}

              <div>
                <label className="block text-[10px] md:text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Name</label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  className="w-full px-4 py-3 md:px-5 md:py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white outline-none font-semibold text-slate-900"
                  placeholder="e.g. Q4 Growth Roadmap"
                  required
                />
              </div>
              <div>
                <label className="block text-[10px] md:text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Summary</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  className="w-full px-4 py-3 md:px-5 md:py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white outline-none h-24 resize-none text-slate-700"
                  placeholder="Scope, objectives, and success criteria..."
                  required
                />
              </div>
              <div>
                <label className="block text-[10px] md:text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Deadline</label>
                <input
                  type="date"
                  value={newProject.deadline}
                  onChange={(e) => setNewProject({ ...newProject, deadline: e.target.value })}
                  className="w-full px-4 py-3 md:px-5 md:py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white outline-none font-semibold text-slate-900"
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] md:text-[11px] font-black uppercase tracking-widest text-slate-400 mb-3 md:mb-4 ml-1">Team Access</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3 max-h-48 overflow-y-auto p-3 md:p-4 bg-slate-50 rounded-2xl border border-slate-200 no-scrollbar">
                  <div className="flex items-center gap-3 p-2.5 rounded-xl border bg-white border-indigo-200 shadow-sm opacity-90 cursor-default ring-2 ring-indigo-500/10">
                    <img src={currentUser?.avatar} className="w-8 h-8 rounded-full border border-indigo-100" alt="" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-black text-indigo-600 truncate">{currentUser?.name}</p>
                      <span className="text-[8px] font-black uppercase tracking-widest text-indigo-400">Owner (Automatic)</span>
                    </div>
                  </div>

                  {users.filter((u: User) => u.id !== currentUser?.id).map((u: User) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => toggleMember(u.id)}
                      className={`flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all group ${
                        selectedMembers.includes(u.id) 
                          ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' 
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <img src={u.avatar} className="w-8 h-8 rounded-full border border-white/20 shadow-sm" alt="" />
                      <div className="min-w-0">
                        <p className={`text-[10px] font-black truncate ${selectedMembers.includes(u.id) ? 'text-white' : 'text-slate-800'}`}>{u.name}</p>
                        <span className={`text-[8px] font-bold uppercase tracking-widest ${selectedMembers.includes(u.id) ? 'text-indigo-200' : 'text-slate-400'}`}>
                          {u.role.replace('_', ' ')}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 md:gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 md:py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-[10px] md:text-[11px] hover:bg-slate-200">Cancel</button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 md:py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] md:text-[11px] disabled:opacity-50 shadow-xl shadow-indigo-600/20 active:scale-95 transition-all"
                >
                  {isSubmitting ? 'Syncing...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
